import React, { useState, useCallback } from 'react';
import { X, Upload, Camera, FileText, Sparkles, Loader2, CheckCircle, Shield, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Guapo character image
const GUAPO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

export function GuapoVisionModal({ isOpen, onClose, onScanComplete }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && isValidFile(file)) {
      handleFileSelect(file);
    }
  }, []);

  const isValidFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    return validTypes.includes(file.type);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setScanComplete(false);
    setScanError(null);
    setExtractedData(null);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFile(file)) {
      handleFileSelect(file);
    }
  };

  const handleScan = async () => {
    if (!selectedFile) return;
    
    setScanning(true);
    setScanError(null);
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Get auth token
      const token = localStorage.getItem('session_token');
      
      // Send to Guapo Vision backend endpoint
      const response = await fetch(`${API}/api/debt/guapo-vision/scan`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to scan document');
      }
      
      if (data.success) {
        setExtractedData(data);
        setScanComplete(true);
        
        // Call the callback with extracted data
        if (onScanComplete) {
          onScanComplete({
            success: true,
            accountName: data.accountName,
            balance: data.balance,
            minPayment: data.minPayment,
            apr: data.apr
          });
        }
      } else {
        setScanError(data.error || 'Could not extract data from image');
      }
    } catch (error) {
      console.error('Guapo Vision error:', error);
      setScanError(error.message || 'Failed to scan document. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setScanComplete(false);
    setScanning(false);
    setScanError(null);
    setExtractedData(null);
    onClose();
  };

  const handleUseData = () => {
    if (extractedData && onScanComplete) {
      onScanComplete({
        success: true,
        useData: true,
        accountName: extractedData.accountName,
        balance: extractedData.balance,
        minPayment: extractedData.minPayment,
        apr: extractedData.apr
      });
    }
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-6 text-white relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="close-guapo-vision-modal"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <img 
                src={GUAPO_IMAGE_URL}
                alt="Guapo"
                className="w-16 h-16 rounded-full object-cover border-3 border-white/50 shadow-lg"
              />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                <Camera className="w-3 h-3 text-yellow-900" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold">Guapo Vision</h2>
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </div>
              <p className="text-white/90 text-sm">AI-Powered Statement Scanner</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Privacy Warning - Task 3 */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-1">Privacy First</p>
                <p className="text-xs text-blue-700">
                  Please only snap a picture of the &quot;Account Summary&quot; box showing your balance and APR. 
                  Keep your name and account number out of the frame! <strong>We never save your images.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4 border border-emerald-200">
            <p className="text-sm text-emerald-800">
              <span className="font-semibold">How it works:</span> Upload a photo of your credit card statement. 
              Guapo will automatically extract your balance, APR, and minimum payment.
            </p>
          </div>

          {/* Error Display */}
          {scanError && (
            <div className="bg-red-50 rounded-xl p-4 mb-4 border border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800 font-medium">Scan Failed</p>
                  <p className="text-xs text-red-700 mt-1">{scanError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Scan Results */}
          {scanComplete && extractedData && (
            <div className="bg-emerald-50 rounded-xl p-4 mb-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="text-emerald-800 font-semibold">Data Extracted Successfully!</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {extractedData.accountName && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Account Name</p>
                    <p className="font-semibold text-gray-900">{extractedData.accountName}</p>
                  </div>
                )}
                {extractedData.balance !== null && extractedData.balance !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500">Balance</p>
                    <p className="font-semibold text-gray-900">${extractedData.balance.toLocaleString()}</p>
                  </div>
                )}
                {extractedData.apr !== null && extractedData.apr !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500">APR</p>
                    <p className="font-semibold text-gray-900">{extractedData.apr}%</p>
                  </div>
                )}
                {extractedData.minPayment !== null && extractedData.minPayment !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500">Min Payment</p>
                    <p className="font-semibold text-gray-900">${extractedData.minPayment.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* File Upload Zone */}
          {!scanComplete && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : selectedFile
                    ? 'border-emerald-400 bg-emerald-50/50'
                    : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
              data-testid="file-drop-zone"
            >
              {scanning ? (
                /* Scanning State */
                <div className="py-4">
                  <Loader2 className="w-12 h-12 mx-auto text-emerald-500 animate-spin mb-4" />
                  <p className="text-emerald-700 font-semibold">Guapo is reading your statement...</p>
                  <p className="text-gray-500 text-sm mt-1">This usually takes a few seconds</p>
                </div>
              ) : selectedFile ? (
                /* File Selected State */
                <div>
                  {previewUrl ? (
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="max-h-32 mx-auto rounded-lg shadow-md mb-4"
                    />
                  ) : (
                    <FileText className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
                  )}
                  <p className="text-gray-900 font-medium">{selectedFile.name}</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      setScanError(null);
                    }}
                    className="mt-3 text-sm text-red-500 hover:text-red-700 font-medium"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                /* Empty State */
                <>
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center">
                    <Upload className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-gray-900 font-medium mb-2">
                    Drag & drop your statement here
                  </p>
                  <p className="text-gray-500 text-sm mb-4">
                    or click to browse files
                  </p>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    onChange={handleInputChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-input"
                  />
                  <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Photos only
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Supported Formats */}
          {!scanComplete && (
            <p className="text-xs text-gray-400 text-center mt-4">
              Supported: JPG, PNG, WEBP, HEIC
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-3">
          {scanComplete && extractedData ? (
            /* Success Actions */
            <>
              <button
                onClick={handleUseData}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all flex items-center justify-center gap-2"
                data-testid="use-extracted-data-btn"
              >
                <CheckCircle className="w-5 h-5" />
                Use This Data
              </button>
              <button
                onClick={() => {
                  setScanComplete(false);
                  setExtractedData(null);
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="w-full py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
              >
                Scan Another Statement
              </button>
            </>
          ) : (
            /* Scan Actions */
            <>
              <button
                onClick={handleScan}
                disabled={!selectedFile || scanning}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="scan-document-btn"
              >
                {scanning ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Scan Document
                  </>
                )}
              </button>
              
              <button
                onClick={handleClose}
                className="w-full py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GuapoVisionModal;
